import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { AiAPI , apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Camera, Upload, Download, Loader2, Flame, ImageIcon, Wand2, Sparkles,
} from "lucide-react";

const PRESETS = [
  { id: "auto", label: "Auto — pro retouch", desc: "Full award-winning retouch pass" },
  { id: "portrait", label: "Portrait — beauty", desc: "Skin, eyes, lips, hair, cinematic light" },
  { id: "product", label: "Product — e-commerce", desc: "Clean bg, kill reflections, sharp" },
  { id: "landscape", label: "Landscape", desc: "Sky, contrast, dehaze, cinematic" },
  { id: "wedding", label: "Wedding", desc: "Warm skin, dreamy highlights" },
  { id: "bw", label: "Black & White", desc: "Fine-art film-like conversion" },
  { id: "hdr", label: "Natural HDR", desc: "Balanced exposure, vivid detail" },
  { id: "restore", label: "Old photo restore", desc: "Repair scratches, fading, damage" },
];

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res((fr.result || "").toString().split(",")[1]);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl; a.download = filename; a.click();
}

export default function PhotoUpgraderPage() {
  const { user, usage, refreshUsage } = useAuth();
  const { toast } = useToast();
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [preset, setPreset] = React.useState("auto");
  const [extra, setExtra] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setResult(null);
    const b64 = await fileToBase64(f);
    setPreview(`data:${f.type};base64,${b64}`);
  };

  const run = async () => {
    if (!user) { toast({ title: "Sign in first", description: "Create a free account to use AI tools." }); return; }
    if (!file) { toast({ title: "Upload a photo", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const r = await AiAPI.photoUpgrade(b64, preset, extra);
      setResult(`data:${r.data.mime_type};base64,${r.data.image_base64}`);
      await refreshUsage();
      toast({ title: "Photo upgraded" });
    } catch (err) {
      toast({ title: "Upgrade failed", description: apiErrorMessage(err, ""), variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="grain">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="chip"><Camera className="h-3 w-3 text-[#4aa4ff]" /> Photo Upgrader</span>
            <h1 className="display-font mt-3 text-3xl font-bold sm:text-4xl">Edit like a <span className="ember-text">world-class photographer</span>.</h1>
            <p className="mt-1 text-sm text-muted-foreground">One-click cinematic retouch. Portraits, products, landscapes, weddings, restoration and more.</p>
          </div>
          {usage && (<div className="chip"><span className="h-1.5 w-1.5 rounded-full bg-[#4aa4ff]" /> {usage.pro ? "Pro · Unlimited" : `${usage.remaining} left this month`}</div>)}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[360px_1fr]">
          <aside className="rounded-2xl border border-border bg-card p-5">
            <Label>Preset</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-muted-foreground">{PRESETS.find((p) => p.id === preset)?.desc}</p>

            <Label className="mt-5 block">Extra instructions (optional)</Label>
            <Textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="e.g. warmer color grade, brighter background" className="mt-1" />

            <Button className="btn-ember mt-5 h-11 w-full gap-2 font-semibold" onClick={run} disabled={busy || !file}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Upgrade photo
            </Button>

            <div className="mt-5 grid grid-cols-2 gap-2">
              {PRESETS.slice(0, 6).map((p) => (
                <button key={p.id} onClick={() => setPreset(p.id)} className={`rounded-lg border p-2 text-left text-xs ${preset === p.id ? "border-[#4aa4ff] bg-[#4aa4ff]/10" : "border-border"}`}>
                  <div className="font-semibold text-foreground">{p.label}</div>
                  <div className="text-muted-foreground">{p.desc}</div>
                </button>
              ))}
            </div>

            <div className="mt-5 flex items-center gap-2 rounded-lg border border-border p-3 text-xs text-muted-foreground">
              <Flame className="h-4 w-4 text-[#4aa4ff]" />
              Pro users get unlimited pro retouches. Free tier uses one export per photo.
            </div>
          </aside>

          <main>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-semibold">Before</div>
                  <Button variant="outline" onClick={() => inputRef.current?.click()} className="h-8 gap-2 text-xs"><Upload className="h-3.5 w-3.5" /> Upload</Button>
                  <input ref={inputRef} type="file" hidden accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} />
                </div>
                <div className="flex min-h-[360px] items-center justify-center rounded-xl bg-white p-3"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => { e.preventDefault(); await onFile(e.dataTransfer.files?.[0]); }}
                >
                  {preview ? <img src={preview} alt="before" className="max-h-[380px] max-w-full rounded-md" /> : (
                    <div className="text-center text-muted-foreground">
                      <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]"><ImageIcon className="h-5 w-5" /></div>
                      <div>Drop or upload a photo</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="mb-2 text-sm font-semibold">After</div>
                <div className="flex min-h-[360px] items-center justify-center rounded-xl bg-white p-3">
                  {busy ? (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-6 w-6 animate-spin text-[#4aa4ff]" /> Editing like an award-winning photographer…
                    </div>
                  ) : result ? (
                    <img src={result} alt="after" className="max-h-[380px] max-w-full rounded-md" />
                  ) : (
                    <div className="text-center text-muted-foreground">Upgraded photo will appear here.</div>
                  )}
                </div>
              </div>
            </div>

            {result && (
              <div className="mt-4 flex flex-wrap gap-3">
                <Button className="btn-ember h-11 gap-2 font-semibold" onClick={() => downloadDataUrl(result, `upgraded_${preset}_${Date.now()}.png`)}>
                  <Download className="h-4 w-4" /> Download
                </Button>
                <Button variant="outline" onClick={() => { setResult(null); }}><Wand2 className="mr-2 h-4 w-4" /> Try different preset</Button>
              </div>
            )}
          </main>
        </div>
      </section>
      <Footer />
    </div>
  );
}
